import classNames from "classnames";
import difference from "lodash/difference";
import groupBy from "lodash/groupBy";
import map from "lodash/map";
import reject from "lodash/reject";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { CustomValuesList } from "PFApp/components/custom_values_list/custom_values_list";
import { useImportanceLabelMap } from "PFApp/hooks/use_importance_label_map";
import { Stack } from "PFComponents/containers";
import { Tile } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { ActivityPureCustomValue, CustomType, Experience, Id, Importance, Profile } from "PFCore/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "../match_attribute_field.module.scss";
import { EmptyCustomValuesMessage } from "./empty_custom_values_message";

type MatchAttributeFieldBoxProps = {
  value: ActivityPureCustomValue[];
  isRankable: boolean;
  disabled: boolean;
  onValueClick: (customValue: ActivityPureCustomValue) => void;
  hasError: boolean;
  allowToAdd?: boolean;
  customType?: CustomType;
  activityCreator?: Profile | null;
  connectedValuesIds?: Id[];
  onChange?: (newValue: ActivityPureCustomValue[]) => void;
};

export const MatchAttributeFieldBox = ({
  value,
  isRankable,
  disabled,
  onValueClick,
  hasError,
  allowToAdd,
  customType,
  activityCreator,
  connectedValuesIds,
  onChange
}: MatchAttributeFieldBoxProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.matchAttributes" });
  const importanceLabelMap = useImportanceLabelMap();

  const itemsRenderData = useMemo(() => {
    const valuesByImportance = groupBy(value, "importance");

    return [Importance.Essential, Importance.Supporting, Importance.Required]
      .map((importance) => ({
        importance,
        title: importanceLabelMap[importance],
        items: valuesByImportance[importance] || []
      }))
      .filter(({ items }) => allowToAdd || items.length > 0);
  }, [importanceLabelMap, value, allowToAdd]);

  const handleChangeValues =
    (importance: Importance, currentItems: ActivityPureCustomValue[]) =>
    (selectedValues: ActivityPureCustomValue[]) => {
      if (!onChange) {
        return;
      }

      const currentIds = map(currentItems, "id");
      const selectedIds = map(selectedValues, "id");

      const removedIds = difference(currentIds, selectedIds);
      const addedIds = difference(selectedIds, currentIds);

      const remainingValues = value.filter((item) => !removedIds.includes(item.id));
      const addedValues = selectedValues
        .filter((val) => addedIds.includes(val.id))
        .map(
          (val) =>
            ({
              ...val,
              importance,
              required_experience: Experience.Intermediate
            }) as ActivityPureCustomValue
        );

      onChange([...remainingValues, ...addedValues]);
    };

  return (
    <Tile
      paddingVariant="none"
      border
      className={classNames({ [css.disabled]: disabled, [css.error]: hasError })}
    >
      {!value.length && !allowToAdd && <EmptyCustomValuesMessage className={css.tileSubsection} center />}
      {itemsRenderData.map(({ importance, title, items }) => {
        const otherImportanceIds = map(reject(value, ["importance", importance]), "id");

        const valuesList = items.length ? (
          <CustomValuesList<ActivityPureCustomValue>
            customValues={items}
            limit={Infinity}
            injectCustomPillProps={(customValue) => ({
              requiredExperience: isRankable ? customValue.required_experience : undefined,
              isRequirement: true,
              onClick: disabled ? undefined : () => onValueClick(customValue)
            })}
          />
        ) : null;

        return (
          <Stack key={importance} className={css.tileSubsection}>
            <Typography variant="labelRegular">{title}</Typography>
            {allowToAdd && customType ? (
              <CustomValuesEditField
                customType={customType}
                handleChange={handleChangeValues(importance, items)}
                values={items}
                parseOptions={(options) =>
                  options.map((option) => ({
                    ...option,
                    ...(otherImportanceIds.includes(option.id) && { disabled: true, selected: true })
                  }))
                }
                renderDisplayValues={() => valuesList}
                displayValuesBelow
                placeholder={t("addSkills")}
                multi
                disabled={disabled}
                isActivity
                activityCreator={activityCreator ?? null}
                forceUnlocked
                closeOnChange={false}
                hideLabel
                hideTip
                connectedValuesIds={connectedValuesIds}
                requiresSearch={customType.name === "skills"}
              />
            ) : (
              valuesList
            )}
            {allowToAdd && !items.length && <EmptyCustomValuesMessage />}
          </Stack>
        );
      })}
    </Tile>
  );
};
